import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * "Complete the look" add-on in the cart drawer.
 * Adds the chosen variant, then hands the refreshed cart sections to the theme
 * through the standard cart update event so the drawer and cart count update.
 */
class SunshineCartAddon extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
  }

  /** Show the photo of the color the shopper picks. */
  #onChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;
    const src = select.selectedOptions[0]?.dataset.img;
    const img = select.closest('[data-sp-item]')?.querySelector('[data-sp-img]');
    if (src && img instanceof HTMLImageElement) img.src = src;
  };

  #onClick = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-sp-add]') : null;
    if (!button || button.disabled) return;

    const row = button.closest('[data-sp-item]');
    const select = row?.querySelector('select');
    const variantId = select?.value || button.dataset.variantId;
    if (!variantId) return;

    const errorEl = this.querySelector('.sp-addon__error');
    if (errorEl) errorEl.hidden = true;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();

      const cartResponse = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const cart = await cartResponse.json();

      if (data.status) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'sunshine-cart-addon' },
        });
        this.dispatchEvent(
          new CartErrorEvent({ error: data.message || 'Add to cart failed', code: 'INVALID' })
        );
        if (errorEl) {
          errorEl.textContent = data.description || data.message || 'Sorry, that could not be added.';
          errorEl.hidden = false;
        }
        button.disabled = false;
        button.removeAttribute('aria-busy');
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: 1,
          sections: data.sections,
          source: 'sunshine-cart-addon',
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);
      if (errorEl) {
        errorEl.textContent = 'Sorry, something went wrong. Please try again.';
        errorEl.hidden = false;
      }
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('sunshine-cart-addon')) {
  customElements.define('sunshine-cart-addon', SunshineCartAddon);
}
